import React, { useState, useEffect } from 'react'
import { Resistance, api } from '../lib/api'
import { Globe, Clock, Target, Edit2, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface Props {
  resistance: Resistance
  onUpdate: (updates: Partial<Resistance>) => void
  onDelete: () => void
}

export function ResistanceCard({ resistance, onUpdate, onDelete }: Props) {
  const navigate = useNavigate()
  const [opensToday, setOpensToday] = useState<number>(0)

  useEffect(() => {
    loadTodaysCount()
  }, [resistance.id])

  const loadTodaysCount = async () => {
    try {
      const data = await api.getTodaysSessions(resistance.id)
      setOpensToday(data.opensToday)
    } catch (err) {
      console.error('Failed to load today\'s count:', err)
    }
  }

  const handleCardClick = () => {
    navigate(`/dashboard/resistance/${resistance.id}`)
  }

  const handleToggleActive = (e: React.MouseEvent) => {
    e.stopPropagation()
    onUpdate({ is_active: !resistance.is_active })
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/dashboard/resistance/${resistance.id}`)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete()
  }

  const limitProgress = Math.min((opensToday / resistance.daily_limit) * 100, 100)
  const isOverLimit = opensToday >= resistance.daily_limit

  return (
    <div
      onClick={handleCardClick}
      className={`bg-white rounded-lg border-2 p-6 transition-all cursor-pointer hover:shadow-md min-h-[220px] ${
        resistance.is_active ? 'border-blue-200 shadow-sm' : 'border-gray-200 opacity-60'
      }`}
    >
      {/* Header with Domain and Toggle/Actions */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`p-2.5 rounded-lg flex-shrink-0 ${resistance.is_active ? 'bg-blue-50' : 'bg-gray-100'}`}>
            <Globe className={`w-5 h-5 ${resistance.is_active ? 'text-blue-600' : 'text-gray-400'}`} />
          </div>
          <h3 className="font-semibold text-gray-900 text-base truncate">{resistance.domain}</h3>
        </div>

        {/* Toggle Switch and Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <span className="text-xs font-medium text-gray-600">
            {resistance.is_active ? 'Active' : 'Inactive'}
          </span>
          <button
            onClick={handleToggleActive}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
              resistance.is_active ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                resistance.is_active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>

          {/* Action Buttons */}
          <button
            onClick={handleEdit}
            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>

          <button
            onClick={handleDelete}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Usage Stats */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Today's opens</span>
          <span className={`text-lg font-semibold ${isOverLimit ? 'text-red-600' : 'text-gray-900'}`}>
            {opensToday}/{resistance.daily_limit}
          </span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isOverLimit ? 'bg-red-500' : opensToday === 0 ? 'bg-gray-300' : 'bg-blue-500'
            }`}
            style={{ width: `${limitProgress}%` }}
          />
        </div>

        {isOverLimit && (
          <p className="text-xs font-medium text-red-600 mt-1.5">Over daily limit</p>
        )}
      </div>

      {/* Settings Row */}
      <div className="flex items-center gap-2.5">
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
          <Target className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            {resistance.daily_limit} opens/day
          </span>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
          <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            {resistance.session_duration_minutes}min/session
          </span>
        </div>
      </div>
    </div>
  )
}